import { ClockArrowUp } from 'lucide-react';
import type { ComponentProps } from 'react';
import { memo, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { Skeleton } from '@/shared/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import { cn } from '@/shared/utils/cn';

import { useGetResponseStats } from '../../hooks/useGetResponseStats';
import { RESPONSE_TIME_TIER_CONFIG } from '../../types/responseStats';

export interface ProfileResponseTimeIndicatorProps extends Omit<
  ComponentProps<'span'>,
  'children'
> {
  userId: string;
  /** When true, displays "You reply within" instead of "Replies within" */
  isOwnProfile?: boolean;
  /** When true, renders a bullet separator before the indicator (only shown when data exists) */
  withBullet?: boolean;
}

export const ProfileResponseTimeIndicator = memo(
  function ProfileResponseTimeIndicator(
    props: ProfileResponseTimeIndicatorProps,
  ) {
    return (
      <ErrorBoundary fallback={null}>
        <Suspense fallback={<ProfileResponseTimeIndicatorSkeleton />}>
          <ProfileResponseTimeIndicatorContent {...props} />
        </Suspense>
      </ErrorBoundary>
    );
  },
);

function ProfileResponseTimeIndicatorContent({
  userId,
  isOwnProfile = false,
  withBullet = false,
  className,
  ...props
}: ProfileResponseTimeIndicatorProps) {
  const { data: stats } = useGetResponseStats({ userId });

  if (!stats?.tier) {
    return null;
  }

  const config = RESPONSE_TIME_TIER_CONFIG[stats.tier];
  const prefix = isOwnProfile ? 'You reply within' : 'Replies within';

  return (
    <>
      {withBullet && <div className="bg-cold-600 size-1 rounded-full" />}
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn('inline-flex items-center gap-1 text-sm', className)}
            style={{ color: config.color }}
            {...props}
          >
            <ClockArrowUp className="size-4" />
            <span>
              {prefix} ~{config.shortLabel}
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <div className="flex flex-col gap-1">
            <p className="font-medium">
              Based on daily initial Duos interactions
            </p>
            <ul className="text-cold-400 list-disc pl-4 text-xs">
              <li>Response time: ~{config.shortLabel}</li>
              <li>
                Reply rate:{' '}
                {stats.responseRatePercent !== null
                  ? `${Math.round(stats.responseRatePercent).toString()}%`
                  : 'N/A'}
              </li>
            </ul>
          </div>
        </TooltipContent>
      </Tooltip>
    </>
  );
}

function ProfileResponseTimeIndicatorSkeleton() {
  return (
    <span className="inline-flex items-center gap-1">
      <Skeleton className="size-4 rounded-full" />
      <Skeleton className="h-4 w-24" />
    </span>
  );
}
